import { useState, useEffect } from 'react';
import DashboardHeader from '../Dashboard/DashboardHeader';
import SettingsDialog from '../Editor/SettingsDialog';
import useAuth from '../../hooks/useAuth';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useDashboard } from '../../context/DashboardContext';
import { useGardenSettings } from '../../context/GardenSettingsContext';
import { setHref } from '../../utils/browserNav';

const NOTIFICATIONS_KEY = 'mgr_email_notifications';

// --- Sub-components ---------------------------------------------------------

function SectionGroup({ title, children }) {
  return (
    <div className="mb-6">
      <div className="px-4 pb-2 text-[11px] font-medium text-gray-500 uppercase tracking-[0.06em]">
        {title}
      </div>
      <div className="bg-white border-t border-b border-gray-200">
        {children}
      </div>
    </div>
  );
}

function SettingsRow({ label, value, action, danger, dim, onClick }) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick(e) : undefined}
      className={`flex items-center justify-between px-4 min-h-[52px] border-b border-gray-100 last:border-b-0 bg-white${onClick ? ' cursor-pointer active:bg-gray-50' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium leading-snug ${danger ? 'text-red-600' : dim ? 'text-gray-600' : 'text-gray-900'}`}>
          {label}
        </div>
        {value && (
          <div className="text-xs text-gray-500 mt-0.5 leading-snug">{value}</div>
        )}
      </div>
      {action !== undefined
        ? <span className="ml-2 flex-none">{action}</span>
        : <span className="text-gray-400 text-sm ml-2">›</span>
      }
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="px-4 py-3.5 border-b border-gray-100 bg-white" aria-hidden>
      <div className="h-3 w-3/5 bg-gray-200 rounded" />
      <div className="h-2.5 w-2/5 bg-gray-200 rounded mt-1.5" />
    </div>
  );
}

function Toggle({ on, disabled, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      onClick={onToggle}
      className={`relative w-11 h-[26px] rounded-full transition-colors flex-none${disabled ? ' opacity-60 cursor-not-allowed' : ' cursor-pointer'}${on ? ' bg-emerald-500' : ' bg-gray-300'}`}
    >
      <span
        className={`absolute top-[3px] w-5 h-5 rounded-full bg-white shadow-sm transition-[left]${on ? ' left-[23px]' : ' left-[3px]'}`}
      />
    </button>
  );
}

function ConfirmDialog({ title, body, danger, primary, secondary, onConfirm, onCancel, requireType }) {
  const [typed, setTyped] = useState('');
  const confirmDisabled = requireType && typed !== 'löschen';

  return (
    <div
      className="absolute inset-0 z-30 bg-black/40 flex items-end justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="w-full max-w-[380px] bg-white rounded-2xl p-5 shadow-xl">
        <div id="confirm-dialog-title" className="text-[17px] font-bold text-gray-900 mb-2">{title}</div>
        <div className="text-[13px] text-gray-600 leading-[1.55] mb-4">{body}</div>
        {requireType && (
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Tippe{' '}
              <code className="text-xs font-semibold text-gray-900 bg-gray-100 px-1 py-0.5 rounded">
                löschen
              </code>{' '}
              zur Bestätigung
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="löschen"
              aria-label="Bestätigungstext eingeben"
              className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm text-gray-900 outline-none focus:border-emerald-500"
            />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`h-11 rounded-lg border-none font-medium text-sm text-white transition-opacity${danger ? ' bg-red-600' : ' bg-emerald-500'}${confirmDisabled ? ' opacity-50 cursor-not-allowed' : ' cursor-pointer'}`}
          >
            {primary}
          </button>
          <button
            onClick={onCancel}
            className="h-11 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm cursor-pointer"
          >
            {secondary}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main SettingsView ------------------------------------------------------

const SettingsView = ({ onClose }) => {
  const {
    user,
    loading: authLoading,
    isAuthenticated,
    logout,
    logoutAll,
    deleteAccount,
    exportData,
  } = useAuth();
  const { isMobile } = useBreakpoint();
  const { setMobileTab } = useDashboard();
  const { settings } = useGardenSettings();

  const [notificationsOn, setNotificationsOn] = useState(
    () => localStorage.getItem(NOTIFICATIONS_KEY) === 'true'
  );
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [saveError, setSaveError] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showLogoutAllDialog, setShowLogoutAllDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);

  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const activeGardenName = settings?.name || 'Mein Garten';

  const handleToggleNotifications = () => {
    if (offline) return;
    const next = !notificationsOn;
    try {
      localStorage.setItem(NOTIFICATIONS_KEY, String(next));
      setNotificationsOn(next);
      setSaveError(false);
    } catch {
      setSaveError(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    setShowLogoutDialog(false);
    setHref('/login');
  };

  const handleLogoutAll = async () => {
    await logoutAll();
    setShowLogoutAllDialog(false);
    setHref('/login');
  };

  const handleDeleteAccount = async () => {
    try {
      await deleteAccount();
      setShowDeleteDialog(false);
      setHref('/login');
    } catch {
      setDeleteError(true);
    }
  };

  const handleExportData = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(false);
    try {
      await exportData();
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  };

  const isAnonymous = !authLoading && !isAuthenticated;

  return (
    <div className="relative flex flex-col h-full bg-gray-50">
      <DashboardHeader />

      {/* Sub-header */}
      <div className="flex items-center gap-2.5 h-12 px-3 bg-white border-b border-gray-200 flex-shrink-0">
        {isMobile && (
          <button
            type="button"
            aria-label="Zurück"
            onClick={() => setMobileTab('uebersicht')}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-900 text-[18px] hover:bg-gray-100 transition-colors"
          >
            ←
          </button>
        )}
        <h2 className="flex-1 text-base font-semibold text-gray-900 m-0">Einstellungen</h2>
        {onClose && (
          <button
            type="button"
            aria-label="Einstellungen schließen"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 text-[18px] hover:bg-gray-100 transition-colors"
          >
            ✕
          </button>
        )}
      </div>

      {/* Scroll content */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ paddingBottom: isMobile ? 80 : 24, paddingTop: 16 }}
      >
        {/* Offline banner */}
        {offline && (
          <div className="mx-4 mb-4 bg-gray-100 rounded-[10px] px-3 py-2.5 flex items-center gap-2.5 text-xs text-gray-700">
            <span className="w-2 h-2 rounded-full bg-gray-500 flex-none" aria-hidden="true" />
            Keine Verbindung — Änderungen werden synchronisiert, sobald du online bist.
          </div>
        )}

        {/* Konto */}
        <SectionGroup title="Konto">
          {authLoading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : isAnonymous ? (
            <SettingsRow
              label="Anmelden"
              value="Speichere deinen Garten dauerhaft"
              action={
                <a
                  href="/login"
                  className="h-9 px-3.5 rounded-lg bg-emerald-500 text-white text-xs font-medium flex items-center no-underline"
                >
                  Anmelden
                </a>
              }
            />
          ) : (
            <SettingsRow
              label="E-Mail"
              value={user?.email || '—'}
              action={<span className="text-[11px] font-mono text-gray-400">read-only</span>}
            />
          )}
        </SectionGroup>

        {/* Gärten */}
        {!isAnonymous && !authLoading && (
          <SectionGroup title="Gärten">
            <SettingsRow
              label="Gärten verwalten"
              value={`1 Garten — ${activeGardenName} aktiv`}
            />
          </SectionGroup>
        )}

        {/* Garteneinstellungen */}
        {!isAnonymous && !authLoading && (
          <SectionGroup title="Garteneinstellungen">
            <SettingsRow
              label="Fläche, Boden, Standort"
              value={`${activeGardenName} anpassen`}
              onClick={() => setSettingsDialogOpen(true)}
            />
          </SectionGroup>
        )}

        {/* Benachrichtigungen */}
        {!isAnonymous && !authLoading && (
          <SectionGroup title="Benachrichtigungen">
            <div className="px-4 py-3.5 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-3">
                  <div className="text-sm font-medium text-gray-900">E-Mail-Erinnerungen</div>
                  <div className="text-xs text-gray-500 mt-1 leading-snug">
                    Wir schreiben dir, wenn Pflegephasen beginnen.
                  </div>
                </div>
                <Toggle
                  on={notificationsOn}
                  disabled={authLoading || offline}
                  onToggle={handleToggleNotifications}
                />
              </div>
              {saveError && (
                <div className="mt-2.5 px-2.5 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-600 leading-snug">
                  Konnte nicht gespeichert werden. Erneut versuchen.
                </div>
              )}
            </div>
          </SectionGroup>
        )}

        {/* Rechtliches */}
        <SectionGroup title="Rechtliches">
          <SettingsRow
            label="Datenschutz"
            action={<span className="text-[11px] text-gray-400">↗</span>}
            onClick={() => window.open('/datenschutz', '_blank', 'noopener')}
          />
          <SettingsRow
            label="Impressum"
            action={<span className="text-[11px] text-gray-400">↗</span>}
            onClick={() => window.open('/impressum', '_blank', 'noopener')}
          />
        </SectionGroup>

        {/* Meine Daten (DSGVO Art. 15/20) */}
        {!isAnonymous && !authLoading && (
          <SectionGroup title="Meine Daten">
            <SettingsRow
              label="Meine Daten exportieren"
              value={
                exportError
                  ? 'Export fehlgeschlagen. Erneut versuchen.'
                  : exporting
                    ? 'Export wird vorbereitet …'
                    : 'Profil und Gärten als JSON-Datei herunterladen'
              }
              action={
                <span className="text-[11px] text-gray-400">
                  {exporting ? '…' : '↓'}
                </span>
              }
              onClick={exporting ? undefined : handleExportData}
            />
          </SectionGroup>
        )}

        {/* Divider + destructive actions */}
        {!isAnonymous && !authLoading && (
          <>
            <div className="mx-4 h-px bg-gray-200 my-2" />

            <div className="bg-white border-t border-b border-gray-200">
              <SettingsRow
                label="Abmelden"
                danger
                action={<span className="w-3.5 inline-block" />}
                onClick={() => setShowLogoutDialog(true)}
              />
              <SettingsRow
                label="Auf allen Geräten abmelden"
                value="Beendet alle aktiven Sitzungen, auch auf anderen Geräten"
                danger
                action={<span className="w-3.5 inline-block" />}
                onClick={() => setShowLogoutAllDialog(true)}
              />
            </div>

            <div className="bg-white border-b border-gray-200 mt-[-1px]">
              <SettingsRow
                label="Konto löschen"
                value="Konto und alle Gärten dauerhaft entfernen"
                danger
                action={<span className="w-3.5 inline-block" />}
                onClick={() => {
                  setDeleteError(false);
                  setShowDeleteDialog(true);
                }}
              />
            </div>
          </>
        )}

        {/* Footer */}
        <div className="py-6 px-4 text-center font-mono text-[11px] text-gray-400">
          Mein Grünraum — v0.4.2
        </div>
      </div>

      {/* Garteneinstellungen dialog */}
      {settingsDialogOpen && (
        <SettingsDialog
          isOpen={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          onEditSettings={() => setSettingsDialogOpen(false)}
          onResetWizard={() => setSettingsDialogOpen(false)}
          onNewGarden={() => setSettingsDialogOpen(false)}
        />
      )}

      {/* Logout confirm dialog */}
      {showLogoutDialog && (
        <ConfirmDialog
          title="Wirklich abmelden?"
          body="Du wirst zur Anmeldeseite weitergeleitet. Deine Daten bleiben erhalten."
          primary="Abmelden"
          secondary="Bleiben"
          danger
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutDialog(false)}
        />
      )}

      {/* Logout-all (revoke every session) confirm dialog (SEC-07, #525) */}
      {showLogoutAllDialog && (
        <ConfirmDialog
          title="Auf allen Geräten abmelden?"
          body="Alle aktiven Sitzungen werden beendet — auch auf diesem Gerät. Du musst dich überall neu anmelden. Deine Daten bleiben erhalten."
          primary="Überall abmelden"
          secondary="Abbrechen"
          danger
          onConfirm={handleLogoutAll}
          onCancel={() => setShowLogoutAllDialog(false)}
        />
      )}

      {/* Delete account confirm dialog (Art. 17 DSGVO, #529) */}
      {showDeleteDialog && (
        <ConfirmDialog
          title="Konto löschen?"
          body={
            deleteError
              ? 'Löschen fehlgeschlagen. Bitte versuche es erneut.'
              : 'Dein Konto und alle Gärten werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
          }
          primary="Konto endgültig löschen"
          secondary="Abbrechen"
          danger
          requireType
          onConfirm={handleDeleteAccount}
          onCancel={() => {
            setDeleteError(false);
            setShowDeleteDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default SettingsView;
